<template>
  <div class="p-4">
    <div v-if="error" role="alert" class="rounded-md bg-red-tint px-3 py-2 text-gray-900">{{ error }}</div>
    <div v-else-if="loading" class="text-gray-500">Loading {{ nounPlural.toLowerCase() }}…</div>
    <section v-else class="rounded-lg border border-gray-200 bg-white" data-test="program-subjects">
      <div class="border-b border-gray-200 px-4 py-3">
        <h3 class="font-semibold text-gray-900">Direct Secure Message subject</h3>
        <p class="mt-1 text-gray-600">
          The subject when someone sends engagement summaries to a provider by Direct Secure Message, and the title of the
          clinical document they receive. It is configurable per {{ noun.toLowerCase() }}; any {{ noun.toLowerCase() }}
          without its own subject uses the default. Senders can still change it before sending.
        </p>
        <div class="mt-3 max-w-3xl rounded-md bg-neutral-100 px-3 py-2 text-xs text-gray-700">
          <p data-test="subject-default"><span class="font-medium text-gray-900">Default:</span> {{ defaultSubject }}</p>
          <table class="mt-2 w-full text-left" data-test="subject-placeholders">
            <thead>
              <tr class="text-[10px] uppercase tracking-wider text-gray-500">
                <th class="py-1 pr-4 font-medium">Type this</th>
                <th class="py-1 pr-4 font-medium">It becomes</th>
                <th class="py-1 font-medium">Example</th>
              </tr>
            </thead>
            <tbody>
              <tr class="border-t border-gray-200">
                <td class="py-1 pr-4"><code class="rounded bg-white px-1">{patient_name}</code></td>
                <td class="py-1 pr-4">The patient's name</td>
                <td class="py-1">Jane Doe</td>
              </tr>
              <tr class="border-t border-gray-200">
                <td class="py-1 pr-4"><code class="rounded bg-white px-1">{program_name}</code></td>
                <td class="py-1 pr-4">The {{ noun.toLowerCase() }}'s name</td>
                <td class="py-1">{{ programs[0]?.name || 'Chronic Disease Management' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <p v-if="!programs.length" class="px-4 py-6 text-gray-600">No care management {{ nounPlural.toLowerCase() }} yet.</p>
      <table v-else class="w-full table-fixed text-left">
        <colgroup>
          <col class="w-72">
          <col>
          <col class="w-28">
        </colgroup>
        <thead>
          <tr class="text-[11px] uppercase tracking-wider text-gray-500">
            <th class="px-4 py-2 font-medium">{{ noun }}</th>
            <th class="px-4 py-2 font-medium">Provider sees</th>
            <th class="px-4 py-2" />
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in programs" :key="p.id" class="align-top" :data-test="`subject-row-${p.id}`">
            <td class="border-t border-gray-100 px-4 py-2 text-gray-900">{{ p.name }}</td>
            <td class="border-t border-gray-100 px-4 py-2">
              <template v-if="editingId === p.id">
                <input
                  v-model="draft"
                  type="text"
                  maxlength="500"
                  class="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
                  :placeholder="defaultSubject"
                  :aria-label="`Direct Secure Message subject for ${p.name}`"
                  :data-test="`subject-input-${p.id}`"
                >
                <p class="mt-1 text-xs text-gray-500">Leave blank to use the default.</p>
                <p class="mt-1 text-xs text-gray-700" :data-test="`subject-preview-${p.id}`">Preview: {{ render(draft.trim() || defaultSubject, p) }}</p>
                <p v-if="subjectError" role="alert" class="mt-1 text-xs text-red-700" data-test="subject-error">{{ subjectError }}</p>
              </template>
              <template v-else>
                <span :class="p.direct_message_subject ? 'text-gray-900' : 'text-gray-500'" :data-test="`subject-text-${p.id}`">{{ render(p.direct_message_subject || defaultSubject, p) }}</span>
                <span v-if="!p.direct_message_subject" class="ml-2 rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] text-gray-600" :data-test="`subject-default-${p.id}`">Default</span>
              </template>
            </td>
            <td class="border-t border-gray-100 px-4 py-2 text-right">
              <div v-if="editingId === p.id" class="flex justify-end gap-2">
                <button type="button" class="text-sm text-gray-600 hover:underline" :data-test="`subject-cancel-${p.id}`" @click="cancelEdit">Cancel</button>
                <button
                  type="button"
                  class="rounded-md bg-primary-600 px-3 py-1 text-sm text-white hover:bg-primary-700 disabled:opacity-50"
                  :disabled="saving"
                  :data-test="`subject-save-${p.id}`"
                  @click="saveSubject(p)"
                >
                  {{ saving ? 'Saving…' : 'Save' }}
                </button>
              </div>
              <button
                v-else-if="editingId === null"
                type="button"
                class="text-sm text-primary-700 hover:underline"
                :data-test="`subject-edit-${p.id}`"
                @click="startEdit(p)"
              >
                Edit
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script>
import { getProgramSettings, updateProgramSummarySubject } from '@/services/api/careManagementPrograms';

export default {
  name: 'ProgramSettings',
  data() {
    return {
      loading: true,
      error: '',
      programs: [],
      defaultSubject: '',
      editingId: null,
      draft: '',
      saving: false,
      subjectError: '',
    };
  },
  computed: {
    noun() {
      return this.$store?.getters['tenant/opportunityNoun'] || 'Program';
    },
    nounPlural() {
      return this.$store?.getters['tenant/opportunityNounPlural'] || 'Programs';
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      try {
        const settings = await getProgramSettings();
        this.programs = settings.programs;
        this.defaultSubject = settings.default_direct_message_subject;
      } catch {
        this.error = 'Programs could not be loaded. Try again in a moment.';
      } finally {
        this.loading = false;
      }
    },
    render(subject, program) {
      return subject.replaceAll('{patient_name}', 'Jane Doe').replaceAll('{program_name}', program.name);
    },
    startEdit(program) {
      this.editingId = program.id;
      this.draft = program.direct_message_subject || '';
      this.subjectError = '';
    },
    cancelEdit() {
      this.editingId = null;
      this.subjectError = '';
    },
    async saveSubject(program) {
      this.saving = true;
      this.subjectError = '';
      try {
        const saved = await updateProgramSummarySubject(program.id, this.draft.trim());
        program.direct_message_subject = saved.direct_message_subject;
        this.editingId = null;
      } catch (e) {
        const problems = e?.response?.data?.direct_message_subject;
        this.subjectError = problems?.[0] || 'The subject could not be saved.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
